import { getDueDiligenceCompanies, type TDueDiligenceCompany } from "@/api/dueDiligence";
import { FilterPillsBar } from "@/components/dashboard/FilterPillsBar";
import { PageHeader } from "@/components/layout";
import { AppCard } from "@/components/ui";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Skeleton } from "@/components/ui/Skeleton";
import { cn } from "@/lib/utils";
import { extractErrorMessage } from "@/utils/errorUtils";
import { getScoreBadgeClass } from "@/utils/scoreBadge";
import { FileText, Info, Paperclip, Send } from "lucide-react";
import { useEffect, useState } from "react";

type TInvestorDueDiligenceFeatureProps = {
  companyId?: string | null;
  /** Section title rendered at the top of the scrollable area. */
  title: string;
  /** Section description rendered under the title. */
  description?: string | null;
};

function DueDiligenceCard({ company }: { company: TDueDiligenceCompany }) {
  const [question, setQuestion] = useState("");

  return (
    <AppCard className="p-4">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <FileText className="h-4 w-4 flex-shrink-0 text-muted-foreground sm:h-5 sm:w-5" />
          <h3 className="truncate text-base font-semibold text-foreground sm:text-lg">{company.name}</h3>
        </div>
        <div
          className={cn(
            "flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full border-2 p-2 text-[10px] font-semibold leading-tight sm:h-12 sm:w-12 sm:text-xs",
            getScoreBadgeClass(company.score)
          )}
        >
          <span className="whitespace-nowrap">{company.score}/100</span>
        </div>
      </div>

      <p className="mb-4 text-sm leading-relaxed text-foreground">{company.summary}</p>

      <div className="mb-4 flex items-center gap-2 text-xs text-muted-foreground">
        <span>Powered by {company.source}</span>
        <Info className="h-4 w-4" />
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        <Button
          size="sm"
          className="bg-primary text-primary-foreground hover:bg-primary/90"
          disabled
          title="Coming soon"
        >
          Request Introduction
        </Button>
        {company.score < 80 && (
          <Button
            size="sm"
            variant="outline"
            className="border-border text-foreground hover:bg-muted"
            disabled
            title="Coming soon"
          >
            Shortlist
          </Button>
        )}
        <Button
          size="sm"
          variant="outline"
          className="border-border text-foreground hover:bg-muted"
          disabled
          title="Coming soon"
        >
          Dismiss
        </Button>
      </div>

      <div
        className="flex items-center gap-2 rounded-lg border border-border bg-card p-2 opacity-50"
        title="Coming soon"
      >
        <Input
          type="text"
          placeholder="Ask a question… (coming soon)"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          className="flex-1 border-0 bg-transparent text-sm focus-visible:ring-0"
          disabled
          aria-disabled="true"
        />
        <button
          className="flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground"
          disabled
          aria-disabled="true"
        >
          <Paperclip className="h-4 w-4" />
        </button>
        <Button
          size="sm"
          className="h-8 bg-primary px-4 text-primary-foreground hover:bg-primary/90"
          disabled
          aria-disabled="true"
        >
          <Send className="h-4 w-4" />
        </Button>
      </div>
    </AppCard>
  );
}

/**
 * My Due Diligences dashboard body. Renders two siblings inside PageLayout's
 * flex column: a fixed category filter bar, then the scrollable area holding
 * the section title/description card and the due-diligence cards.
 */
export function InvestorDueDiligenceFeature({ companyId, title, description }: TInvestorDueDiligenceFeatureProps) {
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [companies, setCompanies] = useState<TDueDiligenceCompany[]>([]);
  const [categories, setCategories] = useState<string[]>(["All"]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!companyId) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    void getDueDiligenceCompanies(companyId)
      .then(({ data, error: err }) => {
        if (cancelled) return;
        setIsLoading(false);
        if (err) {
          void extractErrorMessage(err).then(setError);
          return;
        }
        setCompanies(data.companies);
        setCategories(data.categories);
      })
      .catch((e) => {
        if (cancelled) return;
        setIsLoading(false);
        setError(e instanceof Error ? e.message : "Failed to load");
      });

    return () => {
      cancelled = true;
    };
  }, [companyId]);

  const filteredCompanies =
    selectedCategory === "All" ? companies : companies.filter((company) => company.category === selectedCategory);

  const header = (
    <PageHeader
      title={title}
      subtitle={description ?? undefined}
    />
  );

  if (isLoading) {
    return (
      <>
        {/* Category pills skeleton — fixed slot */}
        <AppCard className="flex-shrink-0 p-4">
          <div className="flex items-center gap-2">
            {[10, 20, 16, 24, 14].map((w, i) => (
              <Skeleton
                key={i}
                className={`h-8 w-${w} rounded-full`}
              />
            ))}
          </div>
        </AppCard>
        <div className="min-h-0 flex-1 overflow-auto">
          <div className="flex flex-col gap-4">
            {header}
            {/* Due diligence card skeletons */}
            {[1, 2].map((i) => (
              <AppCard
                key={i}
                className="p-4"
              >
                {/* Header: icon + name + score badge */}
                <div className="mb-4 flex items-start justify-between gap-3">
                  <div className="flex flex-1 items-center gap-2">
                    <Skeleton className="h-4 w-4 flex-shrink-0 rounded" />
                    <Skeleton className="h-4 w-40" />
                  </div>
                  <Skeleton className="h-11 w-11 flex-shrink-0 rounded-full" />
                </div>
                {/* Summary lines */}
                <div className="mb-4 space-y-2">
                  <Skeleton className="h-3 w-full" />
                  <Skeleton className="h-3 w-full" />
                  <Skeleton className="h-3 w-3/4" />
                </div>
                {/* Powered by line */}
                <div className="mb-4 flex items-center gap-2">
                  <Skeleton className="h-3 w-24" />
                  <Skeleton className="h-4 w-4 rounded" />
                </div>
                {/* Action buttons */}
                <div className="mb-4 flex flex-wrap gap-2">
                  <Skeleton className="h-8 w-36 rounded-md" />
                  <Skeleton className="h-8 w-20 rounded-md" />
                  <Skeleton className="h-8 w-20 rounded-md" />
                </div>
                {/* Ask a question input */}
                <Skeleton className="h-10 w-full rounded-lg" />
              </AppCard>
            ))}
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <div className="min-h-0 flex-1 overflow-auto">
        <div className="flex flex-col gap-4">
          {header}
          <AppCard className="p-8 text-center">
            <p className="text-sm text-destructive">{error}</p>
          </AppCard>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Fixed category filter bar — stays put while the content below scrolls */}
      <div className="flex-shrink-0">
        <FilterPillsBar>
          {categories.map((category) => (
            <button
              key={category}
              onClick={(e) => {
                setSelectedCategory(category);
                e.currentTarget.blur();
              }}
              className={`flex-shrink-0 whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                selectedCategory === category
                  ? "bg-primary text-primary-foreground"
                  : "border border-primary bg-card text-primary hover:bg-primary/10"
              }`}
            >
              {category}
            </button>
          ))}
        </FilterPillsBar>
      </div>

      {/* Scrollable content */}
      <div className="min-h-0 flex-1 overflow-auto">
        <div className="flex flex-col gap-4">
          {header}
          <div className="space-y-4">
            {filteredCompanies.length > 0 ? (
              filteredCompanies.map((company) => (
                <DueDiligenceCard
                  key={company.id}
                  company={company}
                />
              ))
            ) : (
              <AppCard className="bg-muted p-8 text-center">
                <p className="text-sm text-muted-foreground">No companies found for this category.</p>
              </AppCard>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
